import React, { useEffect, useState, useRef } from 'react'
import { useSpring, animated } from '@react-spring/web'

interface TrailPoint {
	x: number
	y: number
	timestamp: number
}

// 모바일 기기 감지 함수
const isMobileDevice = (): boolean => {
	return (
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			navigator.userAgent
		) || window.innerWidth <= 768
	)
}

const ParticleMouseFollower: React.FC = () => {
	const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
	const [trailPoints, setTrailPoints] = useState<TrailPoint[]>([])
	const [isClicking, setIsClicking] = useState(false)
	const lastUpdateTime = useRef(Date.now())

	// 모바일 기기 체크
	const isMobile = isMobileDevice()

	// 메인 원 애니메이션
	const mainProps = useSpring({
		x: mousePos.x - 10,
		y: mousePos.y - 10,
		scale: isClicking ? 1.3 : 1,
		config: { tension: 300, friction: 30 },
	})

	useEffect(() => {
		// 모바일에서는 이벤트 리스너를 등록하지 않음
		if (isMobile) return

		const handleMouseMove = (event: MouseEvent) => {
			const now = Date.now()
			const newPos = { x: event.clientX, y: event.clientY }
			setMousePos(newPos)

			// 트레일 포인트 추가 (속도 조절)
			if (now - lastUpdateTime.current > 16) {
				// 60fps로 제한
				setTrailPoints((prev) => {
					const newTrail = [
						{ x: newPos.x, y: newPos.y, timestamp: now },
						...prev.slice(0, 19), // 최대 20개의 트레일 포인트 유지 (약 1cm 정도)
					]
					return newTrail
				})
				lastUpdateTime.current = now
			}
		}

		const handleMouseDown = () => {
			setIsClicking(true)
		}

		const handleMouseUp = () => {
			setIsClicking(false)
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mousedown', handleMouseDown)
		document.addEventListener('mouseup', handleMouseUp)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mousedown', handleMouseDown)
			document.removeEventListener('mouseup', handleMouseUp)
		}
	}, [isMobile])

	// 오래된 트레일 포인트 정리
	useEffect(() => {
		if (isMobile) return

		const interval = setInterval(() => {
			const now = Date.now()
			setTrailPoints(
				(prev) => prev.filter((point) => now - point.timestamp < 1000) // 1초 후 제거
			)
		}, 50)

		return () => clearInterval(interval)
	}, [isMobile])

	// 모바일에서는 아무것도 렌더링하지 않음
	if (isMobile) {
		return null
	}

	return (
		<>
			{/* 붓글씨 트레일 효과 */}
			{trailPoints.map((point, index) => {
				const age = Date.now() - point.timestamp
				const progress = Math.min(age / 800, 1) // 800ms에 걸쳐 페이드아웃
				const opacity = (1 - progress) * 0.8
				const scale = 1 - progress * 0.7 + 0.3 // 최소 30% 크기까지 축소

				// 각 트레일 포인트마다 다른 크기와 위치 지연
				const delay = index * 0.8 // 각 포인트마다 지연
				const size = Math.max(2 + (1 - progress) * 6, 2) // 2px ~ 8px

				// 색상 선택: 기본 #ec4625, 클릭 시 #0084ad
				const rgbaColor = isClicking
					? `rgba(0, 132, 173, ${opacity})` // #0084ad with opacity
					: `rgba(236, 70, 37, ${opacity})` // #ec4625 with opacity

				return (
					<div
						key={`${point.timestamp}-${index}`}
						className="fixed pointer-events-none rounded-full"
						style={{
							left: point.x - size / 2,
							top: point.y - size / 2,
							width: `${size}px`,
							height: `${size}px`,
							backgroundColor: rgbaColor,
							transform: `scale(${scale})`,
							transition: `all ${delay}ms cubic-bezier(0.4, 0, 0.2, 1)`,
							zIndex: 49 - index, // 뒤쪽 트레일이 더 뒤에 오도록
						}}
					/>
				)
			})}

			{/* 메인 마우스 커서 */}
			<animated.div
				className="fixed pointer-events-none z-50 w-3 h-3 rounded-full shadow-lg"
				style={{
					left: mainProps.x,
					top: mainProps.y,
					transform: mainProps.scale.to((s: number) => `scale(${s})`),
					backgroundColor: isClicking
						? 'rgba(0, 132, 173, 0.9)' // #0084ad when clicking
						: 'rgba(236, 70, 37, 0.9)', // #ec4625 normal
				}}
			/>

			{/* 클릭 시 확산 효과 */}
			{isClicking && (
				<div
					className="fixed pointer-events-none rounded-full border-2 animate-ping"
					style={{
						left: mousePos.x - 12,
						top: mousePos.y - 12,
						width: '24px',
						height: '24px',
						borderColor: 'rgba(0, 132, 173, 0.6)', // #0084ad with opacity
						zIndex: 48,
					}}
				/>
			)}
		</>
	)
}

export default ParticleMouseFollower
